import { Theme, ThemeMode } from "./types";
import { defaultTheme } from "./default";
import { lightTheme } from "./light";
import { StorageKey } from "@/constants/storageKeys";

// Theme registry
export const themes: Record<ThemeMode, Theme> = {
  default: defaultTheme,
  light: lightTheme,
};

// Helper function to get theme by mode
export const getTheme = (mode: ThemeMode): Theme => {
  return themes[mode] || themes.default;
};

// Helper function to convert hex to HSL
const hexToHSL = (hex: string): string => {
  // Remove # if present
  hex = hex.replace("#", "");

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Helper function to apply theme to CSS variables
export const applyTheme = (theme: Theme) => {
  const root = document.documentElement;

  // Apply color variables
  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssVarName = `--theme-color-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });

  // Apply scrollbar color variables specifically
  root.style.setProperty(
    "--theme-scrollbar-track",
    theme.colors.scrollbarTrack,
  );
  root.style.setProperty(
    "--theme-scrollbar-thumb",
    theme.colors.scrollbarThumb,
  );
  root.style.setProperty(
    "--theme-scrollbar-thumb-hover",
    theme.colors.scrollbarThumbHover,
  );
  root.style.setProperty(
    "--theme-scrollbar-thumb-active",
    theme.colors.scrollbarThumbActive,
  );
  root.style.setProperty(
    "--theme-scrollbar-corner",
    theme.colors.scrollbarCorner,
  );

  // Map theme colors to shadcn/ui HSL variables
  root.style.setProperty("--background", hexToHSL(theme.colors.background));
  root.style.setProperty("--foreground", hexToHSL(theme.colors.text));
  root.style.setProperty("--card", hexToHSL(theme.colors.surface));
  root.style.setProperty("--card-foreground", hexToHSL(theme.colors.text));
  root.style.setProperty("--popover", hexToHSL(theme.colors.surface));
  root.style.setProperty("--popover-foreground", hexToHSL(theme.colors.text));
  root.style.setProperty("--primary", hexToHSL(theme.colors.primary));
  root.style.setProperty(
    "--primary-foreground",
    hexToHSL(theme.colors.primaryForeground),
  );
  root.style.setProperty("--secondary", hexToHSL(theme.colors.secondary));
  root.style.setProperty(
    "--secondary-foreground",
    hexToHSL(theme.colors.secondaryForeground),
  );
  root.style.setProperty("--muted", hexToHSL(theme.colors.backgroundSecondary));
  root.style.setProperty(
    "--muted-foreground",
    hexToHSL(theme.colors.textMuted),
  );
  root.style.setProperty("--accent", hexToHSL(theme.colors.accent));
  root.style.setProperty(
    "--accent-foreground",
    hexToHSL(theme.colors.accentForeground),
  );
  root.style.setProperty("--destructive", hexToHSL(theme.colors.danger));
  root.style.setProperty(
    "--destructive-foreground",
    hexToHSL(theme.colors.dangerForeground),
  );
  root.style.setProperty("--border", hexToHSL(theme.colors.border));
  root.style.setProperty("--input", hexToHSL(theme.colors.input));
  root.style.setProperty("--ring", hexToHSL(theme.colors.primary));
  root.style.setProperty("--radius", theme.borderRadius.md);

  // Apply typography variables
  root.style.setProperty("--theme-font-base", theme.typography.fontFamilyBase);
  root.style.setProperty(
    "--theme-font-heading",
    theme.typography.fontFamilyHeading,
  );
  root.style.setProperty("--theme-font-mono", theme.typography.fontFamilyMono);
  root.style.setProperty(
    "--theme-font-display",
    theme.typography.fontFamilyDisplay,
  );

  // Apply font sizes
  Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
    root.style.setProperty(`--theme-font-size-${key}`, value);
  });

  // Apply spacing
  Object.entries(theme.spacing).forEach(([key, value]) => {
    root.style.setProperty(`--theme-spacing-${key}`, value);
  });

  // Apply border radius
  Object.entries(theme.borderRadius).forEach(([key, value]) => {
    root.style.setProperty(`--theme-radius-${key}`, value);
  });

  // Apply shadows
  Object.entries(theme.shadows).forEach(([key, value]) => {
    root.style.setProperty(`--theme-shadow-${key}`, value);
  });

  // Apply animation durations
  Object.entries(theme.animations.duration).forEach(([key, value]) => {
    root.style.setProperty(`--theme-duration-${key}`, value);
  });

  // Apply animation easings
  Object.entries(theme.animations.easing).forEach(([key, value]) => {
    root.style.setProperty(`--theme-easing-${key}`, value);
  });

  // Set theme mode attribute for CSS targeting
  root.setAttribute("data-theme", theme.id);

  // Apply dark/light mode class
  if (theme.isDark) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
};

// Helper function to get theme from localStorage
export const getStoredTheme = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(StorageKey.THEME_MODE);
    if (stored && stored in themes) {
      return stored as ThemeMode;
    }
  } catch (error) {
    console.error("Error reading theme from localStorage:", error);
  }
  return "default";
};

// Helper function to store theme in localStorage
export const storeTheme = (mode: ThemeMode) => {
  try {
    localStorage.setItem(StorageKey.THEME_MODE, mode);
  } catch (error) {
    console.error("Error storing theme in localStorage:", error);
  }
};

// Export all themes and types
export { defaultTheme, lightTheme };
export * from "./types";
